import { useLayoutEffect, useState } from "react";

type useAutosizerScrollStatusOptions = {
  shouldObserveInnerRef?: boolean;
  dependencyList?: any[];
};

/**
 * Tracks overflow and scroll-bottom state for two refs using ResizeObserver and scroll events.
 */
export default function useAutosizerScrollStatus(
  outerRef: React.RefObject<HTMLElement>,
  innerRef: React.RefObject<HTMLElement | null>,
  options: useAutosizerScrollStatusOptions = {}
) {
  const { shouldObserveInnerRef = true, dependencyList = [] } = options;

  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);

  useLayoutEffect(() => {
    const timeout = setTimeout(() => {
      const outer = outerRef.current;
      const inner = innerRef.current;

      if (!outer || (shouldObserveInnerRef && !inner)) return;

      const updateScrollState = () => {
        const scrollHeight = shouldObserveInnerRef
          ? inner!.scrollHeight
          : outer.scrollHeight;

        const { clientHeight, scrollTop } = outer;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

        setIsOverflowing(scrollHeight > clientHeight);
        setIsAtBottom(atBottom);
      };

      updateScrollState(); // Initial check

      const resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(outer);
      if (shouldObserveInnerRef && inner) {
        resizeObserver.observe(inner);
      }

      outer.addEventListener("scroll", updateScrollState);

      return () => {
        resizeObserver.disconnect();
        outer.removeEventListener("scroll", updateScrollState);
      };
    }, 0);

    return () => clearTimeout(timeout);

    //eslint-disable-next-line
  }, [outerRef, innerRef, shouldObserveInnerRef, ...dependencyList]);

  return { isOverflowing, isAtBottom };
}
